/**
 * Schlanke API-Client-Schicht fürs UI (Schritt 5). Spricht die Bun-REST-API
 * über `POST /api?action=<name>` mit JSON-Body — dieselben §4-Verträge wie die
 * Legacy. Das Session-Cookie geht automatisch mit (same-origin). Bei 401 wird
 * `onUnauthorized` ausgelöst (→ Login), sonstige Netz-/Parse-Fehler werfen
 * `ApiError`. Bewusst DOM-arm (nur fetch), damit gegen einen fetch-Mock testbar.
 */

/** Antwort-Hülle der API: immer `{ ok, ... }`. */
export interface ApiResponse {
  ok: boolean;
  error?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface ApiClientOptions {
  base?: string;
  fetch?: FetchLike;
  onUnauthorized?: () => void;
}

export interface ApiClient {
  /** Ruft eine Action; liefert die geparste `{ok,...}`-Antwort (wirft NICHT bei ok:false). */
  call<T extends ApiResponse = ApiResponse>(action: string, body?: Record<string, unknown>): Promise<T>;
}

export function createApiClient(opts: ApiClientOptions = {}): ApiClient {
  const base = opts.base ?? "/api";
  const doFetch: FetchLike = opts.fetch ?? ((input, init) => fetch(input, init));

  async function call<T extends ApiResponse = ApiResponse>(
    action: string,
    body: Record<string, unknown> = {},
  ): Promise<T> {
    let res: Response;
    try {
      res = await doFetch(`${base}?action=${encodeURIComponent(action)}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
        credentials: "same-origin",
      });
    } catch {
      throw new ApiError("Netzwerkfehler – Dienst nicht erreichbar", 0);
    }

    if (res.status === 401) {
      opts.onUnauthorized?.();
      throw new ApiError("Nicht eingeloggt", 401);
    }

    let data: T;
    try {
      data = (await res.json()) as T;
    } catch {
      throw new ApiError("Ungültige Antwort vom Server", res.status);
    }
    return data;
  }

  return { call };
}
